import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { ACCENT_PRESSED } from "@/components/chat/accent-pressed";
import { Button } from "@/components/design/Button";
import { loadOfferedSearchSources, type OfferedSearchSources, type SearchSource } from "@/lib/research-sources";
import { useDelayedUnmount } from "@/lib/use-delayed-unmount";
import { useAgentStore } from "@/stores/agent-store";

const NAMES: Record<SearchSource, string> = { web: "Web", pubmed: "PubMed" };
// The research sources beside the web, each a toggle of its own; more will follow PubMed.
const RESEARCH: SearchSource[] = ["pubmed"];

/**
 * Beside the notebook's globe, while it is on: each connected research source as a word to click on and off,
 * lit like the globe while on (Marvin, 2026-09-29: shown with the globe only, and a click toggle rather than a
 * switch). The globe searches the web; a source clicked on searches that source instead, since a question
 * searches one source at a time (Marvin, 2026-09-28: the web or PubMed, both at once is wrong). Without the web
 * on offer (the active AI provider has no web search), the globe can only mean a research source, so the one on
 * offer is taken and shown on and locked, saying why. A source appears only once the agent-service
 * reports it connected (PubMed: the admin's switch on and the gateway reaching it), and nothing shows while
 * that is unknown.
 */
export function StudioSearchSources({ disabled }: { disabled: boolean }) {
  const intl = useIntl();
  const [offered, setOffered] = useState<OfferedSearchSources | null>(null);
  const picked = useAgentStore((s) => s.searchSource);
  const setSearchSource = useAgentStore((s) => s.setSearchSource);

  useEffect(() => {
    let cancelled = false;
    loadOfferedSearchSources()
      .then((next) => {
        if (!cancelled) setOffered(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const research = useMemo(() => (offered ? RESEARCH.filter((source) => offered[source]) : []), [offered]);
  const webOffered = offered?.web === true;
  // The globe opens the web; where there is none, the question goes to the research source instead of nowhere.
  useEffect(() => {
    if (offered && picked === "web" && !webOffered && research.length) setSearchSource(research[0]!);
  }, [offered, picked, webOffered, research, setSearchSource]);

  // In and out beside the globe as the profile's visibility controls come and go (Marvin, 2026-09-29).
  const shown = Boolean(picked) && research.length > 0;
  const mounted = useDelayedUnmount(shown);
  if (!mounted) return null;
  return (
    <div className={`${shown ? "slide-in-left" : "slide-out-left"} flex items-center gap-1`}>
      {research.map((source) => {
        const on = picked === source;
        const only = !webOffered;
        const hint = only
          ? intl.formatMessage({ id: "studio.sources.onlySource", defaultMessage: "The active AI provider offers no web search: this question searches {source}" }, { source: NAMES[source] })
          : intl.formatMessage({ id: "studio.sources.instead", defaultMessage: "Search {source} instead of the web for this question" }, { source: NAMES[source] });
        return (
          <Button
            key={source}
            id={`studio-source-${source}`}
            variant="ghost"
            size="sm"
            aria-pressed={on}
            title={hint}
            disabled={disabled || only}
            onClick={() => setSearchSource(on ? "web" : source)}
            reserveEmphasis
            className={`rounded-full ${on ? "font-semibold" : ""}`}
            style={on ? ACCENT_PRESSED : undefined}
          >
            {NAMES[source]}
          </Button>
        );
      })}
    </div>
  );
}
